import type { ConfigField } from '../../services/source.service';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { ConfigFieldDescription } from './ConfigFieldDescription';
import { useConfigFieldOptions } from '../../hooks/useSource';

interface OptionsMultiSelectFieldProps {
  configField: ConfigField;
  value: string;
  onChange: (value: string) => void;
  sourceId?: string;
}

/**
 * Multi-select whose options are supplied by the connector via
 * GET /sources/:id/config-options/:field ({ id, label, sublabel }). Stores the
 * selected ids comma-joined — the same format the tap already expects. When no
 * options are available yet (never discovered, not authorized, or a non-option
 * connector), it degrades to a manual comma-separated text input so the field
 * is never a dead end.
 */
export function OptionsMultiSelectField({ configField, value, onChange, sourceId }: OptionsMultiSelectFieldProps) {
  const selectedValues = value ? value.split(',').map((v) => v.trim()).filter(Boolean) : [];
  const { data: options = [], isLoading } = useConfigFieldOptions(sourceId, configField.name, !!sourceId);

  const toggle = (id: string) => {
    const current = new Set(selectedValues);
    if (current.has(id)) {
      current.delete(id);
    } else {
      current.add(id);
    }
    onChange(Array.from(current).join(','));
  };

  const noOptions = !isLoading && options.length === 0;

  return (
    <div className="space-y-2">
      <Label>{configField.label}</Label>

      {isLoading && <p className="text-sm text-muted-foreground">Loading options…</p>}

      {!isLoading && options.length > 0 && (
        <div className="border rounded-md p-2 space-y-1 max-h-64 overflow-y-auto">
          {options.map((opt) => (
            <label
              key={opt.id}
              className="flex items-start gap-2 cursor-pointer px-2 py-1 hover:bg-accent/50 rounded-sm"
            >
              <input
                type="checkbox"
                checked={selectedValues.includes(opt.id)}
                onChange={() => toggle(opt.id)}
                className="mt-0.5 rounded border-input"
              />
              <span className="text-sm leading-tight">
                <span className="font-medium">{opt.label}</span>
                <span className="block text-xs text-muted-foreground">
                  {opt.sublabel ? `${opt.sublabel} · ${opt.id}` : opt.id}
                </span>
              </span>
            </label>
          ))}
        </div>
      )}

      {noOptions && (
        <div className="space-y-1">
          <Input
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Comma-separated IDs"
          />
          <p className="text-xs text-muted-foreground">
            No options loaded yet — run discovery on this source to pick from a list, or enter IDs manually.
          </p>
        </div>
      )}

      {selectedValues.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {selectedValues.map((v) => {
            const opt = options.find((o) => o.id === v);
            return (
              <Badge key={v} variant="secondary" className="text-xs">
                {opt ? opt.label : v}
              </Badge>
            );
          })}
        </div>
      )}

      <ConfigFieldDescription description={configField.description} />
    </div>
  );
}
